import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, API_BASE } from '../../services/apiClient';
import { formatPhone } from '../../utils/phone';

// Kanban board of Work Orders (division='small', app-created jobs). Drag a card
// between columns to set its status; the column is saved on the work order and
// shown as a status pillow. Card face is field-focused: cover photo, job name,
// contact, scope, due date, PM.
export const WO_COLUMNS = [
  { key: 'week_electrical',  label: 'This Week · Electrical',  color: '#1d4ed8', group: 'field' },
  { key: 'week_dirt',        label: 'This Week · Dirt',        color: '#b45309', group: 'field' },
  { key: 'inside_electrical',label: 'Inside Electrical',       color: '#0e7490', group: 'field' },
  { key: 'waiting_material', label: 'Waiting On Material',     color: '#9333ea', group: 'field' },
  { key: 'next_week',        label: 'Next Week Or Longer',     color: '#475569', group: 'field' },
  { key: 'pole_hold',        label: 'Pole Hold',               color: '#dc2626', group: 'field' },
  // Pending came off (Pat, Sep 2026): nothing ever put a work order in it, and the
  // billing half is tracked on Work Orders To Be Billed. A bill that has been prepared
  // and is waiting on a second person (ready_to_send) shows here under To Bill rather
  // than vanishing from the board, which is what it did with no column of its own.
  { key: 'complete_to_bill', label: '100% Complete · To Bill', color: '#15803d', group: 'billing' },
  // Billing / Not Paid and Paid came off this board too (Pat, Sep 22 2026): once a work
  // order is billed it is the office's, tracked on Administration → Work Orders To Be
  // Billed. To Bill stays as the hand-off column.
];
// True on a phone-width screen: one column per screen, swipe for the next.
function useNarrow(px = 640) {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(`(max-width:${px}px)`).matches);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${px}px)`);
    const on = e => setNarrow(e.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, [px]);
  return narrow;
}
const COL_GAP = 12;
const photoUrl = fn => `${API_BASE.replace('/api', '')}/uploads/${fn}`;
const fmtDate = d => { if (!d) return ''; const dt = new Date(String(d).length <= 10 ? d + 'T12:00:00' : d); return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };

// The card component is handed in rather than imported: AdminWorkflows already
// imports this board, and importing it back would make the two modules circular.
export default function WorkOrdersBoard({ cardModal: CardModal, pillows: Pillows }) {
  const [wos, setWos] = useState([]);
  const [openWo, setOpenWo] = useState(null); // the card, opened in place
  // A work order card was pure board state, so there was no way to point anyone at
  // one. It now lives in the address the same way a job's pillow does — which is also
  // what makes "send this page" send the card rather than the whole board.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantCard = searchParams.get('card');
  const wantOpen = searchParams.get('open');   // plans | tm — a section inside the card
  const [loading, setLoading] = useState(true);
  // Which card you are over and which side of it — the same as the drilling board, so
  // the two feel like one thing rather than two different drag behaviours.
  const [overCard, setOverCard] = useState(null);
  const dragRef = React.useRef(null);   // synchronous: state lags a drag

  // Cards someone has placed keep that order; anything never dragged falls in behind
  // in the order the server sent, rather than jumping to the top.
  const byOrder = (a, b) => {
    const A = a.wo_board_order, B = b.wo_board_order;
    if (A == null && B == null) return 0;
    if (A == null) return 1;
    if (B == null) return -1;
    return A - B;
  };

  // target null means the end of the column.
  // Which column a work order sits in — Needs Checking (ready_to_send) sits with To Bill.
  const colOf = w => (w.wo_status === 'ready_to_send' ? 'complete_to_bill' : (w.wo_status || 'next_week'));
  function placeCard(target, colKey, where) {
    const id = dragRef.current;
    dragRef.current = null;
    setDragId(null); setOverCol(null); setOverCard(null);
    if (!id || (target && id === target.id)) return;
    const dragged = wos.find(x => x.id === id);
    if (!dragged) return;
    if (colOf(dragged) !== colKey) moveTo(id, colKey);
    const column = wos.filter(w => colOf(w) === colKey && w.id !== id).sort(byOrder);
    let at = target ? column.findIndex(x => x.id === target.id) : -1;
    if (at < 0) at = column.length;
    else if (where === 'after') at += 1;
    const ids = [...column.slice(0, at), dragged, ...column.slice(at)].map(x => x.id);
    setWos(ws => ws.map(x => { const i = ids.indexOf(x.id); return i < 0 ? x : { ...x, wo_board_order: i }; }));
    apiFetch('/work-orders/board-order', { method: 'POST', body: JSON.stringify({ order: ids }) }).catch(() => {});
  }
  useEffect(() => {
    if (!wantCard || !wos.length) return;
    const w = wos.find(x => x.id === wantCard);
    if (w && (!openWo || openWo.id !== w.id)) {
      setOpenWo({ ...w, _openPlans: wantOpen === 'plans', _openTm: wantOpen === 'tm' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantCard, wantOpen, wos]);

  function openCard(w, section) {
    setOpenWo(w ? { ...w, _openPlans: section === 'plans', _openTm: section === 'tm' } : null);
    const sp = new URLSearchParams(searchParams);
    if (w) { sp.set('card', w.id); if (section) sp.set('open', section); else sp.delete('open'); }
    else { sp.delete('card'); sp.delete('open'); }
    // Replace, so closing a card doesn't need two taps of Back.
    setSearchParams(sp, { replace: true });
  }
  const [err, setErr] = useState('');
  const [pmFilter, setPmFilter] = useState('');
  const [search, setSearch] = useState('');   // Ryan, Oct 14 2026 — like the other boards
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  function reload() {
    apiFetch('/work-orders').then(d => setWos(Array.isArray(d) ? d : [])).catch(e => setErr(e?.error || 'Could not load work orders.')).finally(() => setLoading(false));
  }
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pmOptions = [...new Set(wos.map(w => w.pm_name).filter(Boolean))].sort();
  // Search: work order number, name, customer, contact, PM, scope or comment — every word must match.
  const words = search.trim().toLowerCase().split(/s+/).filter(Boolean);
  const hay = w => `${w.project_number || ''} ${w.name || ''} ${w.client_name || ''} ${w.contact_name || ''} ${w.pm_name || ''} ${w.scope || ''} ${w.wo_comment || ''}`.toLowerCase();
  const shown = wos.filter(w => (!pmFilter || w.pm_name === pmFilter) && (!words.length || words.every(x => hay(w).includes(x))));
  // Only work orders whose field schedule is tracked here (others live on their
  // drilling/loops board). That routing is set on the job's detail page.
  const onBoard = shown.filter(w => (w.schedule_workflow || 'small_jobs') === 'small_jobs' && WO_COLUMNS.some(c => c.key === colOf(w)));

  function moveTo(id, colKey) {
    const wo = wos.find(w => w.id === id);
    if (!wo || colOf(wo) === colKey) return;
    setWos(prev => prev.map(w => w.id === id ? { ...w, wo_status: colKey } : w)); // optimistic
    apiFetch(`/work-orders/${id}`, { method: 'PATCH', body: JSON.stringify({ wo_status: colKey }) })
      .catch(() => { setWos(prev => prev.map(w => w.id === id ? { ...w, wo_status: wo.wo_status } : w)); });
  }

  // Every column is the same height — the room left under the header — and scrolls
  // inside itself, so a long Next Week Or Longer no longer towers over the rest
  // (Pat, Sep 2026). Measured rather than a vh guess so it fits any screen.
  const narrow = useNarrow();
  const boardRef = React.useRef(null);
  const [boardH, setBoardH] = useState(null);
  useLayoutEffect(() => {
    if (loading) return;
    const measure = () => {
      const el = boardRef.current; if (!el) return;
      const scroller = el.closest('.admin-main');
      const top = el.getBoundingClientRect().top + (scroller ? scroller.scrollTop : window.scrollY);
      setBoardH(Math.max(narrow ? 360 : 420, Math.round(window.innerHeight - top - (narrow ? 14 : 34))));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [loading, narrow]);
  // Phone: one column per screen, snap from one to the next; the chip row says where
  // you are and jumps you there.
  const [colIdx, setColIdx] = useState(0);
  const chipRefs = React.useRef({});
  useEffect(() => { chipRefs.current[colIdx]?.scrollIntoView?.({ inline: 'center', block: 'nearest', behavior: 'smooth' }); }, [colIdx]);
  const scrollToCol = i => { const el = boardRef.current; if (el) el.scrollTo({ left: i * (el.clientWidth + COL_GAP), behavior: 'smooth' }); };
  const onBoardScroll = e => { const el = e.currentTarget; const i = Math.round(el.scrollLeft / (el.clientWidth + COL_GAP)); if (i !== colIdx) setColIdx(Math.max(0, Math.min(WO_COLUMNS.length - 1, i))); };

  const colStyle = {
    ...(narrow ? { flex: '0 0 100%', width: '100%', minWidth: 0, scrollSnapAlign: 'start' } : { minWidth: 280, width: 280, flexShrink: 0 }),
    height: boardH || '76vh', background: 'var(--surface-alt,#f8fafc)', borderRadius: 12, border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{onBoard.length} work order{onBoard.length !== 1 ? 's' : ''} · {narrow ? 'swipe for the next column · tap a card to open it' : 'drag a card to change its status'}</div>
        <span style={{ flex: 1 }} />
        <div style={{ position: 'relative', flex: narrow ? '1 1 100%' : '0 1 280px', order: narrow ? 3 : 0 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder='Search job, WO number, customer…'
            style={{ width: '100%', boxSizing: 'border-box', padding: search ? '8px 30px 8px 12px' : '8px 12px', borderRadius: 999, border: `1px solid ${search ? '#1e3a5f' : 'var(--border)'}`, background: 'var(--surface)', fontSize: narrow ? 16 : 13 }} />
          {search && <button onClick={() => setSearch('')} title='Clear search' style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 16, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>}
        </div>
        <select value={pmFilter} onChange={e => setPmFilter(e.target.value)}
          style={{ fontSize: 12, fontWeight: 700, padding: '7px 12px', borderRadius: 999, cursor: 'pointer', border: `1px solid ${pmFilter ? '#1e3a5f' : 'var(--border)'}`, background: pmFilter ? '#eff6ff' : 'var(--surface)', color: pmFilter ? '#1e3a5f' : 'var(--text-muted)' }}>
          <option value="">All PMs</option>
          {pmOptions.map(nm => <option key={nm} value={nm}>{nm}</option>)}
        </select>
      </div>

      {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14, marginBottom: 12 }}>{err}</div>}
      {loading ? (
        <div style={{ color: 'var(--text-muted)', padding: '30px 0' }}>Loading work orders…</div>
      ) : (
        <>
        {narrow && (
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 8, paddingBottom: 2, scrollbarWidth: 'none' }}>
            {WO_COLUMNS.map((col, i) => {
              const n = onBoard.filter(w => colOf(w) === col.key).length; const on = i === colIdx;
              return (
                <button key={col.key} ref={el => { chipRefs.current[i] = el; }} onClick={() => scrollToCol(i)}
                  style={{ flexShrink: 0, fontSize: 12, fontWeight: 800, padding: '6px 11px', borderRadius: 999, border: `1.5px solid ${col.color}`, background: on ? col.color : '#fff', color: on ? '#fff' : col.color, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                  {col.label} · {n}
                </button>
              );
            })}
          </div>
        )}
        <div ref={boardRef} onScroll={narrow ? onBoardScroll : undefined}
          style={narrow
            ? { display: 'flex', gap: COL_GAP, overflowX: 'auto', overflowY: 'hidden', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }
            : { display: 'flex', gap: COL_GAP, overflowX: 'auto', paddingBottom: 12 }}>
          {WO_COLUMNS.map((col, ci) => {
            const cards = onBoard.filter(w => colOf(w) === col.key).sort(byOrder);
            return (
              <div key={col.key} style={{ ...colStyle, outline: overCol === col.key ? `2px dashed ${col.color}` : 'none' }}
                onDragOver={e => { e.preventDefault(); setOverCol(col.key); if (overCard) setOverCard(null); }}
                onDragLeave={() => setOverCol(c => c === col.key ? null : c)}
                onDrop={e => { e.preventDefault(); placeCard(null, col.key, 'after'); }}>
                <div style={{ padding: '10px 12px', borderBottom: `3px solid ${col.color}`, display: 'flex', alignItems: 'center', gap: 8, position: 'sticky', top: 0, background: 'var(--surface-alt,#f8fafc)', borderRadius: '12px 12px 0 0' }}>
                  {narrow && <button onClick={() => scrollToCol(ci - 1)} disabled={ci === 0} style={{ background: 'none', border: 'none', fontSize: 20, lineHeight: 1, color: ci === 0 ? '#e5e7eb' : col.color, cursor: 'pointer', padding: '0 4px 0 0' }}>‹</button>}
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: col.color, lineHeight: 1.2 }}>{col.label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 800, color: '#6b7280', background: '#fff', border: '1px solid var(--border)', borderRadius: 10, padding: '0 7px' }}>{cards.length}</span>
                  {narrow && <button onClick={() => scrollToCol(ci + 1)} disabled={ci === WO_COLUMNS.length - 1} style={{ background: 'none', border: 'none', fontSize: 20, lineHeight: 1, color: ci === WO_COLUMNS.length - 1 ? '#e5e7eb' : col.color, cursor: 'pointer', padding: '0 0 0 4px' }}>›</button>}
                </div>
                <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                  {cards.map(w => <WoCard key={w.id} w={w} onOpen={() => openCard(w)} Pillows={Pillows} onChanged={reload}
                    onDragStart={() => { dragRef.current = w.id; setDragId(w.id); }}
                    onDragEnd={() => { dragRef.current = null; setDragId(null); setOverCard(null); }}
                    dragging={dragId === w.id}
                    dropSide={overCard && overCard.id === w.id ? overCard.where : null}
                    onDragOverCard={e => {
                      if (!dragRef.current || dragRef.current === w.id) return;
                      e.preventDefault(); e.stopPropagation();
                      // Top half above, bottom half below — otherwise the end of a
                      // column can never be reached.
                      const r = e.currentTarget.getBoundingClientRect();
                      const where = e.clientY > r.top + r.height / 2 ? 'after' : 'before';
                      if (!overCard || overCard.id !== w.id || overCard.where !== where) setOverCard({ id: w.id, where });
                    }}
                    onDropCard={e => { e.preventDefault(); e.stopPropagation(); placeCard(w, col.key, overCard && overCard.where); }}
                  />)}
                  {cards.length === 0 && <div style={{ fontSize: 11, color: '#c4c9d2', textAlign: 'center', padding: '14px 0', fontStyle: 'italic' }}>Drop here</div>}
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {openWo && CardModal && (
        <CardModal card={openWo} onClose={() => openCard(null)} onChanged={reload} />
      )}
    </div>
  );
}

// Owner / Executive only: take a work order off every board without it ever being
// billed — a test job, a duplicate, work that was called off. Asks why, and the reason
// goes in the job's activity. The server refuses this from anyone else.
const IS_EXEC = (() => { try { return ['admin', 'Executive'].includes(JSON.parse(localStorage.getItem('titan_session') || 'null')?.role); } catch { return false; } })();
function closeWithoutBilling(w, onChanged) {
  const reason = window.prompt(`Close ${w.project_number} — ${w.name || ''} WITHOUT billing it?

It comes off every board. Everything on it stays readable, and it can be reopened.

Why is it being closed?`, 'Test job');
  if (reason === null) return;
  if (!reason.trim()) { window.alert('It needs a reason.'); return; }
  apiFetch(`/work-orders/${w.id}/close`, { method: 'POST', body: JSON.stringify({ force: true, reason: reason.trim() }) })
    .then(() => onChanged && onChanged())
    .catch(e => window.alert(e?.error || e?.message || 'That did not close.'));
}

function WoCard({ w, onOpen, onDragStart, onDragEnd, dragging, Pillows, onChanged, dropSide, onDragOverCard, onDropCard }) {
  const contact = [w.contact_name, formatPhone(w.contact_phone)].filter(Boolean).join(' · ');
  return (
    <div draggable onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={onOpen}
      onDragOver={onDragOverCard} onDrop={onDropCard}
      style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
        // Drawn inside the card: as a border it would grow the card and nudge it out
        // from under the pointer mid-drag, which is what made this feel unsteady.
        boxShadow: dropSide
          ? (dropSide === 'after' ? 'inset 0 -3px 0 #e87722' : 'inset 0 3px 0 #e87722')
          : '0 1px 2px rgba(0,0,0,0.05)',
        opacity: dragging ? 0.4 : 1 }}>
      {w.wo_cover && (
        <div style={{ height: 92, background: `#eef2f7 url(${photoUrl(w.wo_cover)}) center/cover no-repeat` }} />
      )}
      <div style={{ padding: '9px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ fontSize: 10.5, fontWeight: 800, color: '#e87722', fontFamily: 'monospace' }}>{w.project_number}</span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111', lineHeight: 1.25 }}>{w.name || '(untitled)'}</div>
        {contact && <div style={{ fontSize: 11.5, color: '#374151', marginTop: 4 }}>👤 {contact}</div>}
        {w.scope && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{w.scope}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
          {w.pm_name && <span style={{ fontSize: 10, fontWeight: 700, color: '#1e3a5f', background: '#eff6ff', borderRadius: 10, padding: '2px 8px' }}>PM: {w.pm_name}</span>}
          {w.sched_finish && <span style={{ fontSize: 10, fontWeight: 700, color: '#b45309', background: '#fffbeb', borderRadius: 10, padding: '2px 8px' }}>📅 Due {fmtDate(w.sched_finish)}</span>}
          {w.wo_status === 'waiting_material' && (w.open_po_count || 0) === 0 && (
            <span title="Flagged waiting on material but no open purchase orders" style={{ fontSize: 10, fontWeight: 800, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '2px 8px' }}>⚠ No open POs</span>
          )}
        </div>
        {Pillows && (
          <div onClick={e => e.stopPropagation()}
            onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
            style={{ marginTop: -2 }}>
            <Pillows w={{ ...w, project_id: w.id }} compact onChanged={onChanged} />
          </div>
        )}
        {IS_EXEC && (
          <button onClick={e => { e.stopPropagation(); closeWithoutBilling(w, onChanged); }}
            title="Owner / Executive only — for a test job, a duplicate, or work that was called off"
            style={{ display: 'block', width: '100%', background: 'none', border: 'none', padding: '7px 0 0', cursor: 'pointer', fontSize: 10.5, fontWeight: 700, color: '#9ca3af', textAlign: 'right' }}>
            ✕ Close without billing
          </button>
        )}
      </div>
    </div>
  );
}
